import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Loader, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconChevronDown,
  IconChevronRight,
  IconSearch,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import "@mantine/dates/styles.css";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import SubNav from "../shared/Navigation/SubNav";
import { WELCOME_SURVEY_TABS } from "./welcomeSurveyTabs";
import {
  ALL_OPTION,
  accentFilterStyles,
  filterSelectStyles,
} from "./WelcomeSurveySummary";
import { useOfficeOptions } from "./useOfficeOptions";
import { DEFAULT_REP_TARGET } from "./WelcomeSurveyRepQuality";
import { invertedBand, valueBand } from "./welcomeSurveyBands";
import "./WelcomeSurveyRepQualityPage.css";

// The window the report opens on, matching the endpoint's own default of the
// last seven days — the same default the Report tab uses, so switching tabs
// doesn't silently change the period the figures cover.
function defaultDateRange() {
  return [
    dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    dayjs().format("YYYY-MM-DD"),
  ];
}

// Which accounts the report is built from. "All accounts" is every account the
// reps sold in the window; the others narrow it to the accounts that reached
// that stage, or, for pre-service, the ones that have not reached the first of
// them yet.
export const ACCOUNT_FILTERS = [
  { value: "all_accounts", label: "All accounts" },
  { value: "initial_completed", label: "Initial completed" },
  { value: "welcome_survey_completed", label: "Welcome survey completed" },
  { value: "preservice_accounts", label: "Pre-service Accounts" },
];

// The flags the endpoint has always taken. It narrows on every one set, so the
// picked option is sent true and the others false rather than left off.
// `all_accounts` overrides the others there, so sending it alone is what asks
// for the whole window back.
const ESTABLISHED_FLAGS = [
  "all_accounts",
  "initial_completed",
  "welcome_survey_completed",
];

/// The body each option asks the report for.
///
/// `preservice_accounts` is sent only when it is the option picked, rather than
/// as `false` alongside the rest. The api runs its validation pipe with
/// `forbidNonWhitelisted`, so a body carrying a field the endpoint's DTO does
/// not declare is rejected outright — and that DTO does not declare this one
/// yet. Sending it on every request would take the whole tab down until the
/// endpoint catches up; sending it only here leaves the three established
/// filters working today and confines the wait to the new option.
const ACCOUNT_FILTER_FLAGS = Object.fromEntries(
  ACCOUNT_FILTERS.map(option => [
    option.value,
    {
      ...Object.fromEntries(
        ESTABLISHED_FLAGS.map(flag => [flag, flag === option.value])
      ),
      ...(option.value === "preservice_accounts"
        ? { preservice_accounts: true }
        : {}),
    },
  ])
);

const DEFAULT_FILTERS = {
  dateRange: defaultDateRange(),
  officeId: ALL_OPTION,
  accountFilter: "all_accounts",
};

// Percentages come back to two decimal places; the board shows whole numbers,
// so a column of rates lines up as a column rather than as ragged decimals.
function formatPercent(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${Math.round(number)}%` : "—";
}

function formatCount(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString() : "—";
}

// Average contract value, to the dollar. The cents the endpoint reports are
// noise next to a figure that is already an average of whole contracts.
function formatMoney(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function repName(rep) {
  return [rep?.first_name, rep?.last_name].filter(Boolean).join(" ").trim();
}

// Every office on the rep's roster, not only the ones they sold through — a
// rep with none reads as unassigned rather than as a blank line.
function officeLabel(rep) {
  const names = (rep?.offices ?? [])
    .map(office => office?.name)
    .filter(Boolean);
  return names.length > 0 ? names.join(", ") : "No office";
}

/// The board's columns, in the order they are drawn. The unit toggle changes
/// which figure a column shows — its label along with it, where the two units
/// are not the same measure — and nothing else: the rows keep the order the
/// endpoint sent them in whichever unit is on.
const COLUMNS = [
  { key: "retention", label: () => "Retention" },
  {
    key: "welcome",
    label: unit => (unit === "percent" ? "Welcome %" : "Welcome #"),
  },
  { key: "ror", label: () => "RORs" },
  { key: "cancel", label: () => "Cancels" },
  { key: "acv", label: () => "Avg ACV" },
  { key: "score", label: () => "Quality Score" },
];

/// One figure on the board, in the unit asked for: "percent" and "count" show
/// that one alone, and "both" shows the rate with the fraction behind it.
///
/// The unit toggle feeds the three rate columns, so each shows only what is
/// being asked for. Retention is passed "both" outright rather than the
/// toggle's value — it is the column the board is read down, and a rate is
/// worth little there without the number of accounts it is over.
function MetricCell({ unit, count, total, percentage, tone }) {
  const rate = formatPercent(percentage);
  const fraction = `${formatCount(count)}/${formatCount(total)}`;

  return (
    <span className="ws-rqp-figure">
      <span className={`ws-rqp-tone-${tone}`}>
        {unit === "count" ? fraction : rate}
      </span>
      {unit === "both" && (
        <>
          <span className="ws-rqp-figure-sep" aria-hidden="true">
            ·
          </span>
          {fraction}
        </>
      )}
    </span>
  );
}

/// The five headline figures for the window, summed over the accounts rather
/// than averaged over the reps — a rep with two sales does not weigh the same
/// as one with forty, and the endpoint totals them the same way.
function summaryCards(data, reps, unit, target) {
  const total = data?.summary_total_accounts ?? 0;
  const scored = reps.filter(rep => rep.quality_score !== null);
  const below = scored.filter(rep => rep.quality_score < target).length;

  const rate = (count, percentage, noun) => ({
    value: unit === "percent" ? formatPercent(percentage) : formatCount(count),
    note:
      unit === "percent"
        ? `${formatCount(count)} of ${formatCount(total)} ${noun}`
        : `${formatPercent(percentage)} of ${formatCount(total)} ${noun}`,
  });

  return [
    {
      label: "Retention",
      tone: valueBand(data?.summary_active_percentage),
      ...rate(
        data?.summary_active_accounts,
        data?.summary_active_percentage,
        "accounts"
      ),
    },
    {
      label: "Welcome",
      tone: valueBand(data?.summary_ws_percentage),
      ...rate(
        data?.summary_ws_accounts,
        data?.summary_ws_percentage,
        "completed"
      ),
    },
    {
      label: "RORs",
      // Banded on the accounts that did NOT rescind, so a low ROR reads green.
      tone: invertedBand(data?.summary_ror_percentage),
      ...rate(
        data?.summary_ror_accounts,
        data?.summary_ror_percentage,
        "accounts"
      ),
    },
    {
      label: "Cancels",
      // Banded like RORs, on the accounts that did NOT cancel.
      tone: invertedBand(data?.summary_cancelled_percentage),
      ...rate(
        data?.summary_cancelled_accounts,
        data?.summary_cancelled_percentage,
        "accounts"
      ),
    },
    {
      // Already a 0-100 figure, so the unit toggle leaves it alone — there is
      // no count behind a score to switch to.
      label: "Avg Quality Score",
      tone:
        data?.summary_quality_score === null ||
        data?.summary_quality_score === undefined
          ? "neutral"
          : valueBand(data.summary_quality_score),
      value:
        data?.summary_quality_score === null ||
        data?.summary_quality_score === undefined
          ? "—"
          : Math.round(data.summary_quality_score),
      note: `${below} of ${scored.length} below target`,
    },
  ];
}

/**
 * The Rep Quality tab: every welcome-survey rep who sold inside the reporting
 * window, with what became of those sales. Its own page rather than a panel on
 * the Report tab, because it comes from its own endpoint — report-rep-quality
 * groups the window's accounts by the rep they are credited to, and carries the
 * accounts behind each row so a rep opens without a second request.
 */
function WelcomeSurveyRepQualityPage({ repTarget = DEFAULT_REP_TARGET }) {
  const { getVerifiedJWT } = useAuth();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  // Rates read as the scoreboard; counts read as the workload behind it.
  const [unit, setUnit] = useState("percent");
  // The rep whose panel is open, or null for none. One at a time: the panel
  // sits beside the board rather than inside it, so a second rep replaces the
  // first rather than stacking under it.
  const [openRepId, setOpenRepId] = useState(null);
  const officeOptions = useOfficeOptions();

  const { dateRange, officeId, accountFilter } = filters;

  useEffect(() => {
    let cancelled = false;
    const [startDate, endDate] = dateRange ?? [null, null];

    // Mid-selection in the range picker — wait for the second date rather than
    // firing a request for a half-picked window.
    if (startDate && !endDate) return undefined;

    const loadReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint("/v2/welcome-survey/report-rep-quality"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              office_id:
                !officeId || officeId === ALL_OPTION ? null : Number(officeId),
              start_date: startDate ?? null,
              end_date: endDate ?? null,
              ...(ACCOUNT_FILTER_FLAGS[accountFilter] ??
                ACCOUNT_FILTER_FLAGS.all_accounts),
            }),
          }
        );

        if (!response.ok)
          throw new Error("Failed to load the rep quality report");

        const result = await response.json();
        if (cancelled) return;
        setReport(result?.data ?? result);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || "Failed to load the rep quality report");
        setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT, dateRange, officeId, accountFilter]);

  const reps = useMemo(() => report?.reps ?? [], [report]);

  // The endpoint's own order, kept as sent — the board is the report, not a
  // re-ranking of it. Search only takes rows out, so the reps that remain stay
  // in the order they arrived in, and the rank column down the left counts the
  // report's own ranking rather than one this page invented.
  const query = search.trim().toLowerCase();
  const visibleReps = useMemo(
    () =>
      query
        ? reps.filter(
            rep =>
              repName(rep).toLowerCase().includes(query) ||
              officeLabel(rep).toLowerCase().includes(query)
          )
        : reps,
    [reps, query]
  );

  // Looked up in the whole report rather than in visibleReps, so searching the
  // board doesn't close the panel out from under the reader.
  const openRep = useMemo(
    () => reps.find(rep => rep.rep_id === openRepId) ?? null,
    [reps, openRepId]
  );

  const cards = useMemo(
    () => summaryCards(report, reps, unit, repTarget),
    [report, reps, unit, repTarget]
  );

  const setFilter = (key, value) =>
    setFilters(current => ({ ...current, [key]: value }));

  return (
    <PageContent
      customHeader={
        <SubNav
          title="Welcome Survey"
          tabs={WELCOME_SURVEY_TABS}
          activeId="rep-quality"
          ariaLabel="Welcome survey sections"
        />
      }
    >
      <div className="ws-rqp">
        <div className="ws-rqp-header">
          <h2 className="ws-rqp-title">
            Rep Quality
            {loading && <Loader size="xs" ml="sm" display="inline-block" />}
          </h2>
          <div className="ws-rqp-header-controls">
            <DatePickerInput
              type="range"
              value={dateRange}
              onChange={value => setFilter("dateRange", value)}
              valueFormat="MMM D, YYYY"
              allowSingleDateInRange
              radius="xl"
              aria-label="Reporting window"
              placeholder="Pick dates"
              rightSection={<IconChevronDown size={16} />}
              rightSectionPointerEvents="none"
              styles={accentFilterStyles}
            />
            {/* Rates or the counts behind them. Both are on the board either
                way — this picks which of the two leads. */}
            <div
              className="ws-rqp-unit"
              role="group"
              aria-label="Show rates or counts"
            >
              {[
                { value: "percent", label: "%", title: "Show rates" },
                { value: "count", label: "#", title: "Show counts" },
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`ws-rqp-unit-option${
                    unit === option.value ? " ws-rqp-unit-active" : ""
                  }`}
                  aria-pressed={unit === option.value}
                  title={option.title}
                  onClick={() => setUnit(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="ws-rqp-cards">
          {/* Until a report lands there is nothing to total, so the tiles hold
              their shape on dashes rather than reading as a window in which
              nothing happened. */}
          {cards.map(card => (
            <div className="ws-rqp-card" key={card.label}>
              <div className="ws-rqp-card-label">{card.label}</div>
              <div
                className={`ws-rqp-card-value ws-rqp-tone-${
                  report && !error ? card.tone : "neutral"
                }`}
              >
                {report && !error ? card.value : "—"}
              </div>
              <div className="ws-rqp-card-note">
                {error ? "Unavailable" : report ? card.note : "—"}
              </div>
            </div>
          ))}
        </div>

        <section className="ws-rqp-board">
          <div className="ws-rqp-board-header">
            <h3 className="ws-rqp-title">All Reps</h3>
          </div>

          <div className="ws-rqp-controls">
            <label className="ws-rqp-search">
              <IconSearch size={15} />
              <input
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search reps or offices"
                aria-label="Search reps or offices"
              />
            </label>
            {/* Both narrow the accounts the whole report is built from, so
                they move the headline figures as well as the rows — hence a
                reload rather than a filter over what is already on screen. The
                search box beside them is the opposite: it only hides rows, so
                it stays local. */}
            <div className="ws-rqp-filters">
              <Select
                data={officeOptions}
                value={officeId}
                onChange={value => setFilter("officeId", value ?? ALL_OPTION)}
                allowDeselect={false}
                radius="xl"
                aria-label="Filter by office"
                styles={filterSelectStyles}
              />
              <Select
                data={ACCOUNT_FILTERS}
                value={accountFilter}
                onChange={value =>
                  setFilter("accountFilter", value ?? "all_accounts")
                }
                allowDeselect={false}
                radius="xl"
                aria-label="Filter accounts"
                styles={filterSelectStyles}
              />
            </div>
          </div>

          <div className="ws-rqp-table-scroll">
            <table className="ws-rqp-table">
              <thead>
                <tr>
                  <th className="ws-rqp-rank-col" scope="col">
                    <span className="ws-rqp-sr-only">Rank</span>
                  </th>
                  <th scope="col">Rep</th>
                  {COLUMNS.map(column => (
                    <th key={column.key} scope="col" className="ws-rqp-metric">
                      {column.label(unit)}
                    </th>
                  ))}
                  <th className="ws-rqp-open-col" scope="col">
                    <span className="ws-rqp-sr-only">Accounts</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleReps.map((rep, index) => (
                  <RepRow
                    key={rep.rep_id}
                    rep={rep}
                    rank={index + 1}
                    unit={unit}
                    repTarget={repTarget}
                    selected={openRepId === rep.rep_id}
                    onOpen={() => setOpenRepId(rep.rep_id)}
                  />
                ))}
              </tbody>
            </table>

            {visibleReps.length === 0 && (
              <div className="ws-rqp-empty">
                {loading
                  ? "Loading reps…"
                  : error
                    ? error
                    : query
                      ? `No reps match “${search.trim()}”.`
                      : "No rep sold in this window."}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* The opened rep reads beside the board, not inside it. `openRep` is
          looked up in the report rather than held in state, so a reload of the
          window closes a panel whose rep is no longer in it. */}
      <Drawer
        opened={Boolean(openRep)}
        onClose={() => setOpenRepId(null)}
        position="right"
        size={460}
        withCloseButton={false}
        padding={0}
        styles={{ body: { padding: 0, height: "100%" } }}
      >
        {openRep && (
          <RepPanel
            rep={openRep}
            repTarget={repTarget}
            onClose={() => setOpenRepId(null)}
          />
        )}
      </Drawer>
    </PageContent>
  );
}

/// One rep on the board. Opening the row opens their panel beside it; the
/// accounts behind the figures come down with the report, so it costs no
/// request.
function RepRow({ rep, rank, unit, repTarget, selected, onOpen }) {
  const name = repName(rep);
  const hasScore =
    rep.quality_score !== null && rep.quality_score !== undefined;
  const band = hasScore ? valueBand(rep.quality_score) : "none";

  return (
    <tr
      className={`ws-rqp-row${selected ? " ws-rqp-row-open" : ""}`}
      onClick={onOpen}
    >
      <td className="ws-rqp-rank-col">{rank}</td>
      <td>
        <div className="ws-rqp-name" title={name}>
          {name}
        </div>
        <div className="ws-rqp-office">{officeLabel(rep)}</div>
      </td>

      <td className="ws-rqp-metric">
        <MetricCell
          unit="both"
          count={rep.active_sale_count}
          total={rep.total_sale_count}
          percentage={rep.retention_percentage}
          tone="neutral"
        />
        {/* The bar is retention, so the column reads as a set of filled
              gauges at a glance rather than as a column of numbers. */}
        <span className="ws-rqp-bar">
          <span
            className={`ws-rqp-bar-fill ws-rqp-bar-${valueBand(
              rep.retention_percentage
            )}`}
            style={{
              width: `${Math.min(Math.max(rep.retention_percentage || 0, 0), 100)}%`,
            }}
          />
        </span>
      </td>

      <td className="ws-rqp-metric">
        <MetricCell
          unit={unit}
          count={rep.welcome_survey_count}
          total={rep.total_sale_count}
          percentage={rep.welcome_survey_percentage}
          tone={valueBand(rep.welcome_survey_percentage)}
        />
      </td>

      <td className="ws-rqp-metric">
        <MetricCell
          unit={unit}
          count={rep.ror_sale_count}
          total={rep.total_sale_count}
          percentage={rep.ror_percentage}
          tone={invertedBand(rep.ror_percentage)}
        />
      </td>

      <td className="ws-rqp-metric">
        <MetricCell
          unit={unit}
          count={rep.cancelled_sale_count}
          total={rep.total_sale_count}
          percentage={rep.cancel_percentage}
          tone={invertedBand(rep.cancel_percentage)}
        />
      </td>

      <td className="ws-rqp-metric">
        <span className="ws-rqp-figure">{formatMoney(rep.avg_acv)}</span>
      </td>

      <td className="ws-rqp-metric">
        {hasScore ? (
          <span
            className={`ws-rqp-score ws-rqp-tone-${band}`}
            title={
              rep.quality_score < repTarget
                ? `Below the ${repTarget} target`
                : undefined
            }
          >
            {Math.round(rep.quality_score)}
          </span>
        ) : (
          // Null is "no completed survey to judge", not a zero — a dash keeps
          // it out of the score bands rather than ranking the rep last.
          <span
            className="ws-rqp-unknown"
            title="No completed survey in this window to score"
          >
            —
          </span>
        )}
      </td>

      <td className="ws-rqp-open-col">
        <button
          type="button"
          className="ws-rqp-open"
          aria-haspopup="dialog"
          aria-label={`Open the accounts ${name} sold`}
          onClick={event => {
            event.stopPropagation();
            onOpen();
          }}
        >
          <IconChevronRight size={16} />
        </button>
      </td>
    </tr>
  );
}

/// The opened rep, beside the board rather than inside it: who they are, the
/// three rates the board leads on, and every account those rates are counted
/// from. A panel rather than a row that unfolds — the accounts are a list to
/// read down, and pushing the board apart to show them costs the reader the
/// rep's place in it.
function RepPanel({ rep, repTarget, onClose }) {
  const name = repName(rep);
  const hasScore =
    rep.quality_score !== null && rep.quality_score !== undefined;
  const accounts = rep.accounts_sold ?? [];

  return (
    <div className="ws-rqp-panel">
      <header className="ws-rqp-panel-head">
        <div className="ws-rqp-panel-ident">
          <div className="ws-rqp-panel-office">{officeLabel(rep)}</div>
          <h2 className="ws-rqp-panel-name">{name}</h2>
          {/* A rep with no completed survey has no score to state, and saying
              so here would put an explanation where the reader is looking for
              a name and an office. The line is simply left off — the board's
              own dash in the Quality Score column is where that gap is
              reported. */}
          {hasScore && (
            <p
              className={`ws-rqp-panel-score${
                rep.quality_score < repTarget ? " ws-rqp-panel-score-low" : ""
              }`}
            >
              Quality score {Math.round(rep.quality_score)}
            </p>
          )}
        </div>
        <button type="button" className="ws-rqp-panel-close" onClick={onClose}>
          Close
        </button>
      </header>

      {/* The three rates the board leads on, as percentages in both unit modes:
          the panel is one rep rather than a column to compare down, so the
          counts it would otherwise show are already in the list below. */}
      <div className="ws-rqp-panel-stats">
        <PanelStat
          label="Welcome"
          value={formatPercent(rep.welcome_survey_percentage)}
        />
        <PanelStat label="ROR" value={formatPercent(rep.ror_percentage)} />
        <PanelStat
          label="Retention"
          value={formatPercent(rep.retention_percentage)}
        />
      </div>

      <section className="ws-rqp-panel-accounts">
        <h3 className="ws-rqp-panel-label">
          Accounts sold
          <span className="ws-rqp-panel-count">
            {formatCount(accounts.length)}
          </span>
        </h3>
        {accounts.length === 0 ? (
          <p className="ws-rqp-panel-empty">
            No account behind these figures was reported.
          </p>
        ) : (
          <ul className="ws-rqp-panel-list">
            {accounts.map(account => (
              <li
                className="ws-rqp-panel-account"
                key={account.fr_subscription_id}
              >
                <span
                  className={`ws-rqp-panel-dot ws-rqp-panel-dot-${account.status}`}
                  aria-hidden="true"
                />
                <span className="ws-rqp-panel-account-body">
                  <span className="ws-rqp-panel-account-name">
                    {/* FieldRoutes hasn't synced the customer onto the
                        subscription yet — the sale is real, the name isn't
                        known. */}
                    {account.customer_name ??
                      `Subscription ${account.fr_subscription_id}`}
                  </span>
                  <span className="ws-rqp-panel-account-meta">
                    {account.customer_id !== null &&
                    account.customer_id !== undefined
                      ? `#${account.customer_id} · ${account.status}`
                      : account.status}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/// One of the panel's three rates: a label over a figure, in the cream tile the
/// headline cards use, so the panel reads as the same report as the board.
function PanelStat({ label, value }) {
  return (
    <div className="ws-rqp-panel-stat">
      <div className="ws-rqp-panel-stat-label">{label}</div>
      <div className="ws-rqp-panel-stat-value">{value}</div>
    </div>
  );
}

export default WelcomeSurveyRepQualityPage;
